import React from 'react';
import { FaHeart, FaPlane, FaCompass, FaMapMarkerAlt, FaRoute, FaFacebookF, FaPinterestP, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const Footer = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'About Us', href: '/about', view: 'about', color: 'hover:text-primary-500' },
    { label: 'FAQ', href: '/faq', view: 'faq', color: 'hover:text-golden-500' },
    { label: 'How To', href: '/howto', view: 'howto', color: 'hover:text-accent-500' },
    { label: 'Privacy Policy', href: '/privacy', view: 'privacy', color: 'hover:text-adventure-500' },
    { label: 'Terms of Service', href: '/terms', view: 'terms', color: 'hover:text-secondary-500' },
    { label: 'Contact Us', href: '/contact', view: 'contact', color: 'hover:text-coral-500' },
    { label: 'Become an Affiliate', href: '/affiliate', view: 'affiliate', color: 'hover:text-primary-600' },
    { label: 'Blog', href: '/blog', view: 'blog', color: 'hover:text-secondary-500' },
    { label: 'Pricing', href: '/pricing', view: 'pricing', color: 'hover:text-accent-500' },
    { label: 'Sitemap', href: '/sitemap.xml', color: 'hover:text-neutral-500' },
  ];

  const handleLinkClick = (e, link) => {
    if (link.view && onNavigate) {
      e.preventDefault();
      onNavigate(link.view);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-white to-neutral-50 border-t-2 border-gradient-to-r from-primary-200 via-accent-200 to-secondary-200 mt-auto relative overflow-hidden">
      {/* Top gradient border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 via-accent-400 to-secondary-400"></div>

      {/* Decorative background elements */}
      <div className="absolute top-4 right-8 text-primary-100 opacity-60">
        <FaPlane className="text-4xl transform -rotate-12" />
      </div>
      <div className="absolute bottom-4 left-12 text-accent-100 opacity-50">
        <FaCompass className="text-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center group cursor-pointer">
              <img src="/logo.png" alt="ItinerarySeek" className="h-12 w-auto group-hover:scale-105 transition-all duration-300" />
            </div>
            <p className="text-neutral-600 text-sm leading-relaxed max-w-md">
              Your personal AI-powered travel companion. Create <span className="text-adventure-600 font-medium">unforgettable journeys</span> with
              intelligent itineraries tailored to your preferences.
            </p>
            <div className="flex items-center gap-2 text-neutral-500 text-sm">
              <FaMapMarkerAlt className="text-secondary-400" />
              <p className="italic">
                "Adventure awaits those who plan wisely."
              </p>
            </div>
            {/* Social Media Links */}
            <div className="flex items-center gap-3 pt-2">
              <a href="https://x.com/ItinerarySeek?s=09" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-900 text-neutral-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="X (Twitter)">
                <FaXTwitter className="text-sm" />
              </a>
              <a href="https://www.facebook.com/share/1KgrmiR8YG/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#1877F2] text-neutral-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="Facebook">
                <FaFacebookF className="text-sm" />
              </a>
              <a href="https://www.instagram.com/itineraryseek?igsh=dHo4dmtqYjBybmIz" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] text-neutral-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="Instagram">
                <FaInstagram className="text-sm" />
              </a>
              <a href="https://www.tiktok.com/@itineraryseek.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-900 text-neutral-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="TikTok">
                <FaTiktok className="text-sm" />
              </a>
              <a href="https://youtube.com/@itineraryseek?si=u-ZAaQPAHNF08vYw" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#FF0000] text-neutral-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="YouTube">
                <FaYoutube className="text-sm" />
              </a>
              <a href="https://pin.it/ENVZ7poev" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-[#E60023] text-neutral-500 hover:text-white flex items-center justify-center transition-all duration-300 hover:scale-110" title="Pinterest">
                <FaPinterestP className="text-sm" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FaRoute className="text-accent-500" />
              <h4 className="font-semibold font-display text-neutral-800 text-lg">Quick Links</h4>
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link)}
                    className={`text-neutral-600 ${link.color} transition-all duration-300 text-sm inline-flex items-center group font-medium`}
                  >
                    <span className="w-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500 group-hover:w-4 transition-all duration-300 mr-0 group-hover:mr-2 rounded-full"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-neutral-200 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
          <p className="text-neutral-600 text-sm flex items-center font-medium">
            © {currentYear} ItinerarySeek. Made with
            <FaHeart className="text-coral-500 mx-1.5 animate-pulse" />
            for travelers worldwide.
          </p>
          <div className="flex items-center gap-2 text-neutral-500 text-xs bg-gradient-to-r from-primary-50 to-accent-50 px-4 py-2 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-adventure-400 to-accent-400 rounded-full animate-pulse"></span>
            Powered by AI for intelligent trip planning
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
